import type { FC } from 'react';
import { Suspense, useCallback, useState } from 'react';
import type { ActionFieldsOptions, ActionIndexProps } from '../../types';
import { observer } from 'mobx-react-lite';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import actionFieldsVisibility from '../../utils/actionFieldsVisibility';
import { Box, Skeleton } from '@mui/material';
import { SubdirectoryArrowRight } from '@mui/icons-material';
import ActionTypeFieldSelect from './ActionTypeFieldSelect';
import SubActionsType from './SubActionsType';
import SubActionMetrics from './SubActionMetrics';
import SubActionValue from './SubActionValue';

export const SubActionValueField: FC<ActionIndexProps> = observer(({ indexAction, indexVariant, indexTask }) => {
  const tasksFormStore = useCurrentTasksFormStore();
  const storedActionValue = tasksFormStore.getActionValues({ indexTask, indexAction, indexVariant })?.action;
  const { isSubActionVisible } = actionFieldsVisibility(storedActionValue as string);
  const storedSubValue = tasksFormStore.getSubActionValues({ indexTask, indexAction, indexVariant }).value;

  const [actionFieldsOptions, setActionFieldsOptions] = useState<ActionFieldsOptions>(() =>
    storedSubValue && isNaN(Number(storedSubValue)) ? 'metric' : 'absolute'
  );

  const handleActionTypeField = useCallback(
    (value: ActionFieldsOptions) => {
      setActionFieldsOptions(value);
    },
    [setActionFieldsOptions]
  );

  return isSubActionVisible ? (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box
          sx={{
            flex: '0 0 40%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            flexWrap: 'nowrap',
            pl: 1.5,
            borderRight: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,
          }}
        >
          <SubdirectoryArrowRight color="primary" />
          <SubActionsType indexAction={indexAction} indexTask={indexTask} indexVariant={indexVariant} />
        </Box>
        <Box
          sx={{
            flex: '0 0 42px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRight: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,
          }}
        >
          <ActionTypeFieldSelect
            onChange={handleActionTypeField}
            value={actionFieldsOptions}
            disabled={tasksFormStore?.propsRestriction?.disabled}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          {actionFieldsOptions === 'metric' ? (
            <Suspense
              fallback={
                <Box sx={{ p: 0.5, display: 'flex', width: '100%', maxHeight: 36 }}>
                  <Skeleton
                    width={'100%'}
                    height={26}
                    sx={{ transform: 'translateY(0)', borderRadius: '4px', m: 0, p: 0 }}
                  />
                </Box>
              }
            >
              <SubActionMetrics indexAction={indexAction} indexTask={indexTask} indexVariant={indexVariant} />
            </Suspense>
          ) : (
            <SubActionValue indexAction={indexAction} indexTask={indexTask} indexVariant={indexVariant} type="text" />
          )}
        </Box>
      </Box>
    </>
  ) : null;
});

export default SubActionValueField;
